import React, { useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import IconButton from 'components/common/IconButton';
import { errorNotification, handleApiError, successNotification } from 'utils';
import { uploadEmetQuestion } from 'api/files/files';

/**
 * @component UploadEMET
 * @description
 * Componente para importar un archivo Excel (.xls o .xlsx) con preguntas EMET y subirlo al servidor.
 * Permite al usuario seleccionar, visualizar, subir y resetear un archivo.
 * Utiliza `react-bootstrap`, `redux` y helpers personalizados.
 *
 * @param {function} onSubmitFile - Función callback que se llama después de subir el archivo exitosamente.
 */
function UploadEMET({ onSubmitFile }) {
	const token = useSelector(state => state.authentication.user?.token);
	const [file, setFile] = useState(null);
	const fileInputRef = useRef(null);

	/**
	 * @function fetchUploadFiles
	 * @description Sube el archivo al servidor mediante la API `uploadEmetQuestion`.
	 * @param {FormData} form - FormData con el archivo a subir.
	 * @param {string} newFileName - Nombre del archivo.
	 */
	const fetchUploadFiles = async (form, newFileName) => {
		try {
			const response = await uploadEmetQuestion(form, token);
			if (response.data.status === 200) {
				successNotification("Archivo cargado correctamente.");
				onSubmitFile(response.data);
				handleFileReset();
			} else {
				errorNotification("No es posible subir el archivo.");
			}
		} catch (error) {
			handleApiError(error);
		}
	};

	/**
	 * @function handleFileChange
	 * @description Valida la extensión del archivo y actualiza el estado.
	 * @param {Event} event - Evento de cambio de input file.
	 */
	const handleFileChange = (event) => {
		const selectedFile = event.target.files[0];

		if (selectedFile) {
			const fileName = selectedFile.name.toLowerCase();
			if (fileName.endsWith('.xls') || fileName.endsWith('.xlsx')) {
				setFile(selectedFile);
			} else {
				errorNotification('Formato de archivo no válido. Por favor, selecciona un archivo en formato xls.');
				setFile(null);
			}
		}
	};

	/**
	 * @function handleFileInputClick
	 * @description Lanza el input file oculto.
	 */
	const handleFileInputClick = () => {
		fileInputRef.current.click();
	};

	/**
	 * @function handleUpload
	 * @description Valida y sube el archivo al servidor.
	 */
	const handleUpload = async () => {
		if (file) {
			const form = new FormData();
			const newFileName = `${file.name}`;
			form.append('uploaded_file', file, newFileName);
			fetchUploadFiles(form, newFileName);
		} else {
			errorNotification("Es necesario seleccionar un archivo.");
		}
	};

	/**
	 * @function handleFileReset
	 * @description Limpia el archivo seleccionado.
	 */
	const handleFileReset = () => {
		setFile(null);
		fileInputRef.current.value = '';
	};

	return (
		<>
			<input
				type="file"
				className="form-control"
				id="submitFile"
				onChange={handleFileChange}
				style={{ display: 'none' }}
				ref={fileInputRef}
			/>
			<IconButton
				variant="falcon-default"
				size="sm"
				icon={file ? "file-upload" : "file-import"}
				transform="shrink-3"
				className="me-2"
				iconAlign="middle"
				onClick={file ? handleUpload : handleFileInputClick}>
				<span className="d-none d-sm-inline-block d-xl-none d-xxl-inline-block ms-1">
					{file ? 'Subir Archivo' : 'Importar'}
				</span>
			</IconButton>
			{file ? (
				<>
					<span className="text-success me-2">{file.name}</span>
					<IconButton
						variant="falcon-default"
						size="sm"
						icon={"times"}
						transform="shrink-3"
						className="me-2"
						iconAlign="middle"
						onClick={handleFileReset}
					/>
				</>
			) : (
				<span className="text-muted"></span>
			)}
		</>
	);
}

export default UploadEMET;